<template>
  <div class="json-redis-panel">
    <div class="workspace-grid two-cols">
      <el-card class="workspace-card" shadow="hover">
        <template #header>
          <div class="card-header"><span>上传 JSON 到 Redis</span></div>
        </template>

        <el-upload
          ref="uploadRef"
          class="upload-block"
          :auto-upload="false"
          :limit="10"
          :on-exceed="handleExceed"
          :on-change="handleFileChange"
          :before-upload="beforeUpload"
          :show-file-list="true"
          accept=".json"
          multiple
        >
          <template #trigger>
            <el-button type="primary">选择 JSON 文件</el-button>
          </template>
          <el-button class="ml-12" @click="cancelFile">清空</el-button>
        </el-upload>

        <div class="info-block">
          <div class="info-row">
            <span class="info-label">文件数量</span>
            <span class="info-value">{{ currentFiles.length || 0 }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">当前文件</span>
            <span class="info-value">{{ currentFile ? currentFile.name : '未选择文件' }}</span>
          </div>
          <div v-if="currentFile" class="info-row">
            <span class="info-label">文件大小</span>
            <span class="info-value">{{ formatFileSize(currentFile.size || 0) }}</span>
          </div>
        </div>

        <div class="tips-box">
          <div class="tip-line">1. 只允许上传 .json 文件。</div>
          <div class="tip-line">2. 写入 Redis DB1（mazda）。</div>
          <div class="tip-line">3. 标准结构会写成：基础路径:name:type。</div>
          <div class="tip-line">4. seachcar 结构会写成：基础路径:seachdata。</div>
        </div>
      </el-card>

      <el-card class="workspace-card" shadow="hover">
        <template #header>
          <div class="card-header"><span>Redis 路径与结果</span></div>
        </template>

        <div class="path-box">
          <el-input v-model="targetRedisPath" readonly />
          <div class="path-actions">
            <el-button :loading="pathLoading" @click="loadDefaultRedisPath(false)">刷新默认路径</el-button>
            <el-button @click="showRedisPath">修改路径</el-button>
          </div>
        </div>

        <div class="tips-box">
          <div class="tip-line">当前测试版本：{{ currentTestVersion || '-' }}</div>
          <div class="tip-line">默认基础路径：{{ targetRedisPath }}</div>
          <div class="tip-line">示例：{{ targetRedisPath }}:program:10</div>
          <div class="tip-line">字符串值按 Redis String 写入。</div>
        </div>

        <div class="action-row">
          <el-button type="primary" :loading="loading" @click="confirmOnLine">开始上传</el-button>
          <el-button @click="cancelFile">取消</el-button>
        </div>

        <div v-if="jsonUploadResult" class="result-panel">
          <div class="result-row"><span>状态</span><strong>{{ jsonUploadResult.success ? '成功' : '失败' }}</strong></div>
          <div class="result-row"><span>消息</span><strong>{{ jsonUploadResult.message || '-' }}</strong></div>
          <div class="result-row"><span>写入条数</span><strong>{{ jsonUploadResult.writtenCount ?? '-' }}</strong></div>
          <div class="result-row"><span>覆盖条数</span><strong>{{ jsonUploadResult.overwriteCount ?? '-' }}</strong></div>
          <div class="result-row"><span>跳过条数</span><strong>{{ jsonUploadResult.skippedCount ?? '-' }}</strong></div>
        </div>
      </el-card>
    </div>

    <el-card class="workspace-card ab-list-card" shadow="hover">
      <template #header>
        <div class="card-header"><span>写入预览</span></div>
      </template>

      <el-table :data="jsonPreviewKeys" stripe border class="result-table" empty-text="暂无写入预览">
        <el-table-column prop="fileName" label="文件" min-width="180" />
        <el-table-column prop="key" label="Redis Key" min-width="320" show-overflow-tooltip />
        <el-table-column prop="valuePreview" label="值预览" min-width="420" show-overflow-tooltip />
      </el-table>

      <div v-if="jsonErrors.length" class="json-error-box">
        <div class="detail-title">错误信息</div>
        <div v-for="(item, idx) in jsonErrors" :key="idx" class="json-error-line">{{ item }}</div>
      </div>
    </el-card>

    <el-dialog v-model="RedisPathDialog" title="修改 Redis 路径" width="520px">
      <el-input v-model="newRedisPath" placeholder="请输入新的 Redis 路径" />
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="canceUpdataRedisPath">取消</el-button>
          <el-button type="primary" @click="confirmUpdataRedisPath">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { toRef } from 'vue';
import { useMazdaJsonRedis } from '@/composables/mazda/useMazdaJsonRedis';

const props = defineProps({
  serverName: { type: String, default: '' },
  mazdaPost: { type: Function, required: true },
});

const {
  currentTestVersion,
  targetRedisPath,
  newRedisPath,
  RedisPathDialog,
  uploadRef,
  currentFile,
  currentFiles,
  jsonUploadResult,
  jsonPreviewKeys,
  jsonErrors,
  loading,
  pathLoading,
  formatFileSize,
  beforeUpload,
  handleFileChange,
  cancelFile,
  handleExceed,
  confirmOnLine,
  showRedisPath,
  canceUpdataRedisPath,
  confirmUpdataRedisPath,
  loadDefaultRedisPath,
} = useMazdaJsonRedis(props.mazdaPost, toRef(props, 'serverName'));
</script>

<style scoped>
.workspace-grid.two-cols {
  display: grid;
  grid-template-columns: repeat(2, minmax(340px, 1fr));
  gap: 22px;
}

.workspace-card,
.ab-list-card,
.query-card {
  border-radius: 24px;
  border: 1px solid rgba(225, 232, 242, 0.95);
  box-shadow: 0 12px 30px rgba(29, 46, 76, 0.07);
}

.workspace-card :deep(.el-card__body),
.ab-list-card :deep(.el-card__body),
.query-card :deep(.el-card__body) {
  padding-top: 16px;
  padding-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-weight: 800;
  color: #1f2d3d;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.upload-block {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 6px 0 4px;
}

.upload-block :deep(.el-button),
.header-actions :deep(.el-button),
.path-actions :deep(.el-button),
.action-row :deep(.el-button),
.toolbar-actions :deep(.el-button),
.dialog-footer :deep(.el-button) {
  min-width: 96px;
}

.ml-12 {
  margin-left: 12px;
}

.info-block {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fbfcff, #f6f9ff);
  border: 1px solid #ecf2ff;
}

.info-row,
.result-row {
  display: flex;
  gap: 12px;
  line-height: 1.9;
}

.info-label,
.result-row span:first-child {
  width: 90px;
  color: #8a94a6;
  flex-shrink: 0;
}

.info-value,
.result-row strong {
  color: #2f3441;
  word-break: break-all;
}

.path-box {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.path-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.tips-box {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fffaf0, #fff7e6);
  border: 1px solid #ffe4ad;
}

.tip-line {
  line-height: 1.9;
  color: #7a5600;
}

.action-row {
  display: flex;
  gap: 12px;
  margin-top: 22px;
  flex-wrap: wrap;
}

.result-panel {
  min-height: 150px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f7fbff, #f4f8ff);
  border: 1px solid #e8f1ff;
}

.empty-result {
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  border-radius: 16px;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
}

.result-table,
.detail-table {
  width: 100%;
}

.result-table :deep(.el-table__inner-wrapper::before) {
  height: 0;
}

.result-table :deep(.el-table__body-wrapper) {
  max-height: 58vh;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.ab-pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.ab-pagination-left {
  color: #606266;
  font-weight: 600;
}

.ab-pagination-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.page-size-select {
  width: 110px;
}

.jump-input {
  width: 72px;
}

.pager-label {
  color: #606266;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.ab-detail-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fbfcff, #f6f9ff);
  border: 1px solid #ecf2ff;
}

.ab-detail-textarea :deep(.el-textarea__inner) {
  font-family: Consolas, "Courier New", monospace;
  line-height: 1.65;
  border-radius: 18px;
}

@media (max-width: 1180px) {
  .workspace-grid.two-cols {
    grid-template-columns: 1fr;
  }

  .toolbar-actions,
  .header-actions,
  .pagination-wrap,
  .ab-pagination-right {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .ab-detail-meta {
    grid-template-columns: 1fr;
  }
}


.json-error-box {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fff5f4, #fff1ef);
  border: 1px solid #ffd0cb;
}

.json-error-line {
  color: #a8071a;
  line-height: 1.7;
  word-break: break-all;
}

.json-error-line + .json-error-line {
  margin-top: 8px;
}
</style>
